import { useCallback, useEffect, useState } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import BulkUploadModal from "../../../components/ui/BulkUploadModal";
import SuccessView from "../../../components/ui/SuccessView";
import { BulkUploadField } from "../../../lib/bulkUpload";
import { useAddDriverMutation } from "../driverApiSlice";

const DRIVER_BULK_FIELDS: BulkUploadField[] = [
  {
    key: "fullName",
    label: "Full name",
    required: true,
    defaultColumnName: "Full name",
    example: "Name, Driver name",
  },
  {
    key: "email",
    label: "Email address",
    required: true,
    defaultColumnName: "Email address",
    example: "Email",
  },
  {
    key: "phoneNumber",
    label: "Phone number",
    required: true,
    defaultColumnName: "Phone number",
    example: "Phone, Mobile",
  },
  {
    key: "address",
    label: "Address",
    required: true,
    defaultColumnName: "Address",
  },
  {
    key: "licenseExpiryDate",
    label: "License expiry date",
    required: false,
    type: "date",
    defaultColumnName: "License expiry date",
    hint: "Use YYYY-MM-DD or a spreadsheet date.",
    example: "Expiry date, License expiry",
  },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface DriverBulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DriverBulkUploadModal: React.FC<DriverBulkUploadModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [addDriver] = useAddDriverMutation();

  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false);
      setUploadedCount(0);
    }
  }, [isOpen]);

  const validateRecord = useCallback((record: Record<string, unknown>) => {
    const errors: string[] = [];
    const email = String(record.email ?? "").trim();
    if (email && !EMAIL_PATTERN.test(email)) {
      errors.push("Invalid email address");
    }
    return errors;
  }, []);

  const handleUploadRow = async (row: Record<string, unknown>) => {
    const payload = {
      fullName: row.fullName,
      email: row.email,
      phoneNumber: row.phoneNumber,
      address: row.address,
      licenseExpiryDate: row.licenseExpiryDate || undefined,
    };

    try {
      await addDriver(payload).unwrap();
    } catch (error) {
      const message =
        getErrorDetails(error).message || "Failed to add this driver.";
      throw new Error(message);
    }
  };

  const handleUploadComplete = (result: {
    succeeded: number;
    failed: number;
  }) => {
    if (result.failed === 0 && result.succeeded > 0) {
      setUploadedCount(result.succeeded);
      setShowSuccess(true);
    }
  };

  const handleClose = () => {
    setShowSuccess(false);
    setUploadedCount(0);
    onClose();
  };

  if (showSuccess) {
    return (
      <BulkUploadSuccess
        isOpen={isOpen}
        count={uploadedCount}
        onClose={handleClose}
      />
    );
  }

  return (
    <BulkUploadModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Bulk upload drivers"
      entityLabel="drivers"
      fields={DRIVER_BULK_FIELDS}
      storageKey="drivers"
      templateFileName="driver-bulk-upload-template"
      onUploadRow={handleUploadRow}
      onUploadComplete={handleUploadComplete}
      getRowLabel={(row) =>
        [row.fullName, row.email, row.phoneNumber]
          .filter(Boolean)
          .join(" · ")
      }
      validateRecord={validateRecord}
    />
  );
};

const BulkUploadSuccess: React.FC<{
  isOpen: boolean;
  count: number;
  onClose: () => void;
}> = ({ isOpen, count, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed w-full h-full inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-lg shadow-xl max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full mx-4 overflow-hidden">
        <SuccessView
          handleCloseModal={onClose}
          heading=""
          message={`${count} ${
            count === 1 ? "driver has" : "drivers have"
          } been uploaded successfully.`}
        />
      </div>
    </div>
  );
};

export default DriverBulkUploadModal;
